import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '@bo-client/config/config.service';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { map } from 'rxjs/operators';
import { AutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/autocomplete.strategy';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

interface GetCityResult {
  city: string;
  cityId: string;
  orgEinheit: string;
  district: string;
  zipCode: string;
}

interface GetStreetResult {
  city: string;
  cityId: string;
  orgEinheit: string;
  zipCode: string;
  street: string;
  streetId: string;
  sapDistrictNumber: string;
}

export class AdressSenseServiceAutocompleteStrategy implements AutocompleteStrategy {
  constructor(
    public httpClientBack: HttpClient,
    public configService: ConfigService,
    public dataShareService: DataShareService
  ) {}

  getCitySuggestions(lookup: string): Observable<GisDataModel[]> {
    return this.httpClientBack
      .get<GetCityResult>(
        `${this.configService.getSettings('address-sense-service')}/search/cities?plz_city_lookup=${lookup}`,
        {
          headers: {
            'Content-Type': 'application/vnd.susp.v1+json',
            'X-Tenant': this.dataShareService.currentTenant.id,
            'X-Env': 'master'
          }
        }
      )
      .pipe(
        map((adresses: any) => {
          return adresses.map((address) => ({
            city: address.city + (address.district ? ' - ' + address.district : ''),
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode
          }));
        })
      );
  }

  getStreetSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.httpClientBack
      .get<GetStreetResult>(
        `${this.configService.getSettings(
          'address-sense-service'
        )}/search/streets?street_lookup=${val}&city_id=${selectedGisData.cityId}&limit=20&zipCode=${
          selectedGisData.zipCode
        }&city_name=${selectedGisData.city}`,
        {
          headers: {
            'Content-Type': 'application/vnd.susp.v1+json',
            'X-Tenant': this.dataShareService.currentTenant.id,
            'X-Env': 'master'
          }
        }
      )
      .pipe(
        map((adresses: any) => {
          return adresses.map((address) => ({
            city: address.city,
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode,
            sapDistrictNumber: address.sapDistrictNumber,
            street: address.street,
            streetId: address.streetId,
            originalData: address
          }));
        })
      );
  }

  getStreetNoSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.httpClientBack
      .get<GetStreetResult>(
        `${this.configService.getSettings(
          'address-sense-service'
        )}/search/houseNo?houseNo_lookup=${val}&city_id=${selectedGisData.cityId}&limit=20&zipCode=${
          selectedGisData.zipCode
        }&street_id=${selectedGisData.streetId}`,
        {
          headers: {
            'Content-Type': 'application/vnd.susp.v1+json',
            'X-Tenant': this.dataShareService.currentTenant.id,
            'X-Env': 'master'
          }
        }
      )
      .pipe(
        map((adresses: any) => {
          return adresses.map((address) => ({
            city: address.city,
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode,
            sapDistrictNumber: address.sapDistrictNumber,
            originalData: address,
            houseNo: address.houseNo,
            houseNoAddition: address.houseNoAddition,
            availableAreas: address.areas,
            latitude: address.latitude,
            longitude: address.longitude
          }));
        })
      );
  }

  /**
   * Überprüft, ob eine spezifische Area verfügbar ist.sparte
   *
   * @param areaType - Der Typ der Area, die geprüft werden soll.sps
   * @param selectedOption - Das ausgewählte GisDataModel.
   * @return boolean - Gibt true zurück, wenn die Area verfügbar ist, sonst false.
   */
  isAvailableArea(areaType: AreaTypeEnum, selectedOption: GisDataModel): boolean {
    // Wenn keine verfügbaren Areas vorhanden sind, sofort false zurückgeben.
    if (!selectedOption.availableAreas) {
      return false;
    }

    return selectedOption.availableAreas?.some((x) => areaType.toUpperCase() === x.toUpperCase());
  }
}
